import { BrowserModule } from "@angular/platform-browser";
import { NgModule, ErrorHandler, APP_INITIALIZER } from "@angular/core";
import { MessagesModule } from 'primeng/messages';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";
import { LayoutModule } from "./views/layout/layout.module";
import { AuthGuard } from "./core/guard/auth.guard";
import { AppComponent } from "./app.component";
import { ErrorPageComponent } from "./views/pages/error-page/error-page.component";
import { HIGHLIGHT_OPTIONS } from "ngx-highlightjs";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { CustomDateParserFormatterService } from "./providers/custom-date-parser-formatter.service";
import { PerfectScrollbarModule } from "ngx-perfect-scrollbar";
import { NgSelectModule } from '@ng-select/ng-select';
import { ContextMenuModule } from 'primeng/contextmenu';
// search module
// import { Ng2SearchPipeModule } from 'ng2-search-filter';
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";
import { HttpInterceptorInterceptor, DEFAULT_TIMEOUT } from "./interceptor/http-interceptor.interceptor";
import { PasswordResetComponent } from "../app/views/pages/auth/password-reset/password-reset.component";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { NgbDateParserFormatter, NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { SweetAlert2Module } from "@sweetalert2/ngx-sweetalert2";
import { ToastModule } from "primeng/toast";
import { NgxMaskModule, IConfig } from "ngx-mask";
import { NgxIntlTelInputModule } from "ngx-intl-tel-input";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { AccordionModule } from "primeng/accordion";
import { UploadImageDirective } from "./core/directive/upload-image.directive";
import { ViewRecepitComponent } from '../app/views/pages/transaction-management/view-recepit/view-recepit.component';
import { SharedModule } from "./shared/shared.module";
import {ConnectionServiceModule} from 'ng-connection-service';
import { CookieModule } from 'ngx-cookie';
import { datePipeModule } from "./shared/pipe/datePipe.module";
import { GlobalErrorHandler } from "../app/providers/error-handler.service";
import { BillingInvoiceComponent } from './views/pages/billingInvoice/billing-invoice/billing-invoice.component';
import { HostedConfigurationComponent } from './views/pages/hosted-configuration/hosted-configuration.component';
import { ViewInvoiceComponent } from "./views/pages/virtual-terminal/view-invoice/view-invoice.component"; 
import { TableModule } from 'primeng/table';
import { RentalInvoiceComponent } from './views/pages/billingInvoice/rental-invoice/rental-invoice.component';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from 'primeng/api';
import { CancelRepeatedApiInterceptor } from "./interceptor/cancel-repeated-api-interceptor/cancel-repeated-api.interceptor";
import { ApiInterceptor } from "./interceptor/api-interceptor/api.interceptor";
import { ViewReceiptNoAuthComponent } from "./views/pages/transaction-management/view-receipt-no-auth/view-receipt-no-auth.component";
import { CommonService } from "./providers/common.service";
import { environment } from "src/environments/environment";
import { MessageComponent } from './views/pages/message/message.component';
import { MarkdownModule } from "ngx-markdown";
import { TermsCheckComponent } from './views/pages/terms-check/terms-check.component';
import { ChatbotManagementModule } from "./views/pages/chatbot-management/chatbot-management.module";
import { BillingInvoiceModule } from "./views/pages/billingInvoice/billing-invoice.module";
import { ClipboardModule } from '@angular/cdk/clipboard';

const maskConfig: Partial<IConfig> = {
  validation: false,
};

export function initializeApp(commonService: CommonService) {
  const domainName = window.location.origin
  return (): Promise<any> => commonService.getCommonHttpClientParams(`${environment.apiUrl}/getWhitelabelInfo?domainName=${domainName}`).toPromise().then(response => {
    console.log("🚀 ~ return ~ initialzedata:", response)
    localStorage.setItem('whitelabelResData', JSON.stringify(response?.data));
  },(error) => {
    console.log("🚀 ~ return ~ error:", error)
  });
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@NgModule({

  declarations: [
    AppComponent,
    ErrorPageComponent,
    PasswordResetComponent,
    UploadImageDirective,
    ViewRecepitComponent,
    ViewReceiptNoAuthComponent,
    BillingInvoiceComponent,
    ViewInvoiceComponent,
    HostedConfigurationComponent,
    RentalInvoiceComponent,
    MessageComponent,
    TermsCheckComponent
  ],

  imports: [
    PerfectScrollbarModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ConnectionServiceModule,
    MessagesModule,
    LayoutModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    SweetAlert2Module,
    DialogModule,
    ToastModule,
    HttpClientModule,
    ButtonModule,
    NgxIntlTelInputModule,
    AccordionModule,
    SharedModule,
    NgSelectModule,
    NgxMaskModule.forRoot(maskConfig),
    CookieModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: "en",
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    datePipeModule.forRoot(),
    ContextMenuModule,
    TableModule,
    MarkdownModule.forRoot(),
    ChatbotManagementModule,
    BillingInvoiceModule,
    ClipboardModule
  ],
  providers: [
    NgbActiveModal,
    MessageService,
    ToastModule,
    AuthGuard,
    {
      provide: HIGHLIGHT_OPTIONS, // https://www.npmjs.com/package/ngx-highlightjs
      useValue: {
        coreLibraryLoader: () => import("highlight.js/lib/core"),
        // languages: {
        //   xml: () => import('highlight.js/lib/languages/xml'),
        //   typescript: () => import('highlight.js/lib/languages/typescript'),
        //   scss: () => import('highlight.js/lib/languages/scss'),
        // }
      },
    },
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpInterceptorInterceptor,
      multi: true,
    },
    { provide: DEFAULT_TIMEOUT, useValue: 5 * 60 * 1000 },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CancelRepeatedApiInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },

    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatterService },
    CommonService,
    {
        provide: APP_INITIALIZER,
        useFactory: initializeApp,
        deps: [CommonService],
        multi: true
    }
  ],
  bootstrap: [AppComponent],
  exports: [ViewRecepitComponent, ViewReceiptNoAuthComponent]
})
export class AppModule { }
